<template>
	<div>
		<!-- Breadcrumb Area -->
		<div class="breadcrumb-area projects-page-bg">
			<div class="overlay"></div>
			<div class="container">
				<div class="row">
					<div class="col-lg-6">
						<div class="breadcrumb-title">
							<h1>ASDL IoT Projects</h1>
							<p>
								Explore our IoT projects aimed at improving building operations, energy efficiency, and sustainability
								through cellular-connected sensors and real-time data collection.
							</p>
							<router-link to="/contact" class="main-btn primary">Get In Touch</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="breadcrumb-meta">
			<div class="container">
				<ul class="breadcrumb d-flex">
					<li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
					<li class="breadcrumb-item active" aria-current="page">Projects</li>
				</ul>
			</div>
		</div>

		<!-- Projects List -->
		<div class="portfolio-area section-padding">
			<div class="container">
				<div class="row">
					<div class="col-lg-12 wow fadeInUp" data-wow-delay=".2s">
						<div class="section-title text-center">
							<h6>Project Highlights</h6>
							<h2>Our IoT Sensor Projects</h2>
							<p class="mt-3">
								These projects seek to provide lower-cost, higher-reliability Internet of Things (IoT) connected sensors
								to help improve the efficiency of building operations. This data allows building operations to operate
								proactively rather than reactively.
							</p>
						</div>
					</div>
				</div>

				<!-- Grid Layout for Projects -->
				<div class="projects-page-grid mt-4">
					<div class="projects-page-card" v-for="project in projects" :key="project.id">
						<div class="projects-page-single-portfolio-item">
							<div class="portfolio-image">
								<img :src="project.image" :alt="project.name" />
								<div class="portfolio-overlay">
									<h5>{{ project.name }}</h5>
									<p>{{ project.description }}</p>
									<router-link :to="'/projects/' + project.id">View Details</router-link>
								</div>
							</div>
							<div class="portfolio-info">
								<h4>{{ project.name }}</h4>
								<span class="category-badge">{{ project.category }}</span>
								<span class="status-badge" :class="getStatusClass(project.status)">
									{{ project.status }}
								</span>
							</div>
						</div>
					</div>
				</div>

				<!-- About Section -->
				<div class="about-section mt-5 pt-4">
					<div class="row">
						<div class="col-lg-12">
							<h3>About the Projects</h3>
							<p>
								One of the primary hurdles to overcome with uptime reliability is with communication protocol.
								Hard-wired
								devices have a high setup difficulty and low flexibility. Wi-Fi devices are easier to set up but are
								dependent on the Wi-Fi reliability of the building. We sought to use 4G-LTE connected devices for more
								flexibility.
							</p>
							<p>
								Operating outside of Wi-Fi also has the benefit of sustaining operations through power or network
								outages.
								The projects featured here have built-in battery supplies, allowing them to operate for days without
								input
								power.
							</p>
						</div>
					</div>

					<div class="row mt-4">
						<div class="col-lg-12">
							<h4>System Architecture</h4>
							<p>
								The challenge with cellular-based systems is the need for a custom data processing backend. Existing on
								a
								campus' local network allows for easy data publication. Off-network systems require third-party
								processing.
							</p>
							<div class="flow-chart-image mt-3">
								<img src="/assets/images/flow.png" alt="System Flow Chart" class="img-fluid" />
							</div>
						</div>
					</div>
				</div>

				<div class="text-center mt-5">
					<router-link to="/contact" class="main-btn primary">Start a Project with Us</router-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from "axios";

export default {
	name: "ProjectsPage",
	data() {
		return {
			projects: [],
		};
	},
	async mounted() {
		window.scrollTo(0, 0);
		try {
			const response = await axios.get("/assets/data/projects.json");
			this.projects = response.data.projects;
		} catch (error) {
			console.error("Failed to load project data", error);
		}
	},
	methods: {
		getStatusClass(status) {
			switch (status.toLowerCase()) {
				case "active":
					return "status-active";
				case "development":
					return "status-development";
				case "planned":
					return "status-planned";
				default:
					return "";
			}
		},
	},
};
</script>

<style scoped>
/* Layout and structure */
.section-padding {
	padding: 80px 0;
}

.projects-page-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 30px;
	justify-content: center;
	align-items: stretch;
}

.projects-page-card {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.projects-page-single-portfolio-item {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex-grow: 1;
	height: 100%;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
	transition: all 0.3s ease;
	background: #fff;
}

.projects-page-single-portfolio-item:hover {
	transform: translateY(-5px);
	box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Image and overlay */
.portfolio-image {
	position: relative;
	overflow: hidden;
}

.portfolio-image img {
	width: 100%;
	height: 250px;
	object-fit: cover;
	transition: transform 0.5s ease;
}

.projects-page-single-portfolio-item:hover .portfolio-image img {
	transform: scale(1.05);
}

.portfolio-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(30, 60, 114, 0.85);
	color: white;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	padding: 20px;
	opacity: 0;
	transition: opacity 0.3s ease;
}

.projects-page-single-portfolio-item:hover .portfolio-overlay {
	opacity: 1;
}

.portfolio-overlay h5 {
	font-size: 1.2rem;
	font-weight: 700;
	margin-bottom: 10px;
}

.portfolio-overlay p {
	font-size: 0.9rem;
	margin-bottom: 15px;
	max-height: 100px;
	overflow: hidden;
}

.portfolio-overlay a {
	display: inline-block;
	padding: 8px 20px;
	background: white;
	color: #2a5298;
	border-radius: 4px;
	font-weight: 600;
	text-decoration: none;
	transition: all 0.3s ease;
}

.portfolio-overlay a:hover {
	background: #f0f0f0;
}

/* Card content */
.portfolio-info {
	padding: 15px;
	background: white;
}

.portfolio-info h4 {
	font-size: 1.1rem;
	font-weight: 600;
	margin-bottom: 8px;
	word-wrap: break-word;
}

.category-badge,
.status-badge {
	display: inline-block;
	font-size: 0.8rem;
	padding: 4px 10px;
	border-radius: 20px;
}

.category-badge {
	background: #e9f0ff;
	color: #2a5298;
	margin-right: 8px;
}

.status-active {
	background: #e6f7e6;
	color: #28a745;
}

.status-development {
	background: #fff3e0;
	color: #fd7e14;
}

.status-planned {
	background: #e6f0ff;
	color: #007bff;
}

/* Breadcrumb and headers */
.breadcrumb-area {
	background: linear-gradient(to right, #2a5298, #1e3c72);
	color: white;
	padding: 60px 0;
	position: relative;
}

.overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.4);
}

.breadcrumb-title {
	position: relative;
	z-index: 2;
}

.breadcrumb-title h1 {
	font-size: 2.5rem;
	font-weight: bold;
	margin-bottom: 15px;
}

.breadcrumb-meta {
	background: #f4f4f4;
	padding: 15px 0;
}

.section-title h6 {
	color: #2a5298;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 1px;
	margin-bottom: 10px;
}

.section-title h2 {
	font-size: 2rem;
	font-weight: 700;
	color: #333;
}

/* Buttons */
.main-btn {
	display: inline-block;
	padding: 12px 25px;
	border-radius: 5px;
	font-weight: 600;
	text-decoration: none;
	transition: all 0.3s ease;
}

.main-btn.primary {
	background: #2a5298;
	color: white;
}

.main-btn.primary:hover {
	background: #1e3c72;
}

/* About Section */
.about-section {
	background: #f9f9f9;
	padding: 30px;
	border-radius: 8px;
}

.about-section h3 {
	font-size: 1.5rem;
	font-weight: 600;
	margin-bottom: 20px;
	color: #2a5298;
}

.about-section p {
	margin-bottom: 15px;
	line-height: 1.6;
}

.flow-chart-image {
	text-align: center;
}

.flow-chart-image img {
	max-width: 100%;
	border-radius: 6px;
	box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}
</style>